import { Button, Card, Group, Popover, Table, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { useRenameObject, type DataObject } from '@/api/objects';
import { errorMessage } from '@/api/errors';
import { InlineRename } from '@/components/InlineRename';
import { objectName } from './objectName';
import { ButtonLink } from '@/components/links';
import { rowClick, rowStyle } from '@/components/rowClick';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { ObjectStatusBadge } from './ObjectStatusBadge';
import { FindingsList, useFindingMessage, type Finding } from '@/features/runs/FindingsList';
import { useKindLabel } from './useKindLabel';

export interface ObjectsTableProps {
  projectId: string;
  objects: DataObject[];
  /** The caller may open upload sessions (backend `can_upload`). */
  canUpload: boolean;
  /** The project is active; closed projects show no upload actions. */
  projectActive: boolean;
  onDelete?: (obj: DataObject) => void;
}

/** The name cell: plain text, or an inline rename when the caller may rename this object. */
function NameCell({ object }: { object: DataObject }) {
  const rename = useRenameObject(object.public_id);
  const shown = object.name || object.declared_name || object.open_session?.declared_name;
  if (!object.can_rename) return <>{shown || '—'}</>;
  return (
    <InlineRename
      compact
      value={objectName(object)}
      canEdit
      onSave={async (name) => {
        try {
          await rename.mutateAsync(name);
        } catch (err) {
          notifications.show({ color: 'red', message: errorMessage(err) });
          throw err;
        }
      }}
    />
  );
}

/** Client objects table: kind, file, one status badge, size, uploader, and per-row actions. */
export function ObjectsTable({ projectId, objects, canUpload, projectActive, onDelete }: ObjectsTableProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const findingMessage = useFindingMessage();
  const { t: tr } = useTranslation('runs');
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(objects, {
    kind: (o) => kindLabel(o.kind),
    name: (o) => o.name || o.declared_name || o.open_session?.declared_name,
    status: (o) => t(`status.object.${o.client_status}`, { defaultValue: o.client_status }),
    size: (o) => o.total_bytes || o.open_session?.declared_bytes,
    author: (o) => o.uploaded_by?.full_name,
  });

  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('kind')}>{t('objects.kind')}</SortableTh>
            <SortableTh {...sortProps('name')}>{t('objects.name')}</SortableTh>
            <SortableTh {...sortProps('status')}>{t('objects.status')}</SortableTh>
            <SortableTh {...sortProps('size')}>{t('objects.size')}</SortableTh>
            <SortableTh {...sortProps('author')}>{t('objects.uploadedBy')}</SortableTh>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((o) => {
            const uploadable = canUpload && projectActive && !o.deleted && (o.state === 'pending' || o.state === 'uploading');
            return (
              <Table.Tr
                key={o.public_id}
                style={{ ...(uploadable ? rowStyle : {}), ...(o.deleted ? { opacity: 0.6 } : {}) }}
                onClick={
                  uploadable
                    ? rowClick(() => void navigate({ to: '/projects/$projectId/upload/$objectId', params: { projectId, objectId: o.public_id } }))
                    : undefined
                }
              >
                <Table.Td>
                  <Text fz={14} fw={600}>
                    {kindLabel(o.kind)}
                  </Text>
                  {o.notes && (
                    <Text fz={12} c={brand.textMuted}>
                      {o.notes}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <NameCell object={o} />
                </Table.Td>
                <Table.Td>
                  <ObjectStatusBadge object={o} />
                  {!!o.rejection_reason && (
                    <Text fz={12} c="red" mt={4} maw={280}>
                      {findingMessage(o.rejection_reason as Finding)}
                    </Text>
                  )}
                  {(o.findings ?? []).length > 0 && (
                    <Popover width={360} position="bottom-start" shadow="md">
                      <Popover.Target>
                        <UnstyledButton>
                          <Text fz={12} c="forest.6" mt={2}>
                            {tr('findings.details')}
                          </Text>
                        </UnstyledButton>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <FindingsList findings={(o.findings ?? []) as Finding[]} compact />
                      </Popover.Dropdown>
                    </Popover>
                  )}
                </Table.Td>
                <Table.Td>{o.total_bytes ? bytes(o.total_bytes) : o.open_session ? bytes(o.open_session.declared_bytes) : '—'}</Table.Td>
                <Table.Td>{o.uploaded_by?.full_name ?? '—'}</Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    {uploadable && (
                      <ButtonLink size="xs" to="/projects/$projectId/upload/$objectId" params={{ projectId, objectId: o.public_id }}>
                        {o.state === 'uploading' ? t('objects.resumeUpload') : t('objects.continueUpload')}
                      </ButtonLink>
                    )}
                    {o.can_delete && onDelete && (
                      <Button size="xs" color="red" variant="light" onClick={() => onDelete(o)}>
                        {tc('common.remove')}
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
